import React, { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/helper/utils";
import { Input } from "@/components/ui/input";
import { useFormField } from "@/components/ui/form";

export interface InputWithLabelProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "placeholder"> {
  label: string;
  className?: string;
  labelClassName?: string;
}

const InputWithLabel = React.forwardRef<HTMLInputElement, InputWithLabelProps>(
  (
    {
      label,
      className,
      value,
      onChange,
      onBlur,
      onFocus,
      labelClassName,
      ...props
    },
    ref
  ) => {
    const { error } = useFormField();
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const isLabelFloating = isFocused || hasValue;

    // Combine refs
    const combinedRef = (node: HTMLInputElement) => {
      if (inputRef) inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    useEffect(() => {
      setHasValue(Boolean(value && String(value).length > 0));
    }, [value]);

    return (
      <div className="relative">
        <Input
          ref={combinedRef}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(
            "disabled:cursor-not-allowed disabled:opacity-50",
            "transition-all duration-200",
            error && "border-destructive focus-visible:ring-destructive ",
            className
          )}
          {...props}
        />
        <label
          className={cn(
            "absolute right-3 transition-all duration-200 ease-in-out pointer-events-none",
            "text-foreground",
            isLabelFloating
              ? "top-0 -translate-y-1/2 bg-background px-1 text-sm font-medium"
              : "top-1/2 -translate-y-1/2",
            error && isLabelFloating && "text-destructive",
            isFocused && !error && "text-primary",
            props.disabled && "opacity-50",
            labelClassName
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {label}
        </label>
      </div>
    );
  }
);

InputWithLabel.displayName = "InputWithLabel";

export default InputWithLabel;
